import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { companiesAPI } from '../services/api';
import { Company, Job } from '../types';
import { Card, Button, EmptyState, SectionHeader, Pill } from '../components/ui';
import { Icon } from '../components/ui/Icon';
import { SEO } from '../components/seo/SEO';
import { jobUrl, companyUrl, parseJobIdFromSlug } from '../utils/slug';
import { formatJobExperience, hasExperienceRange } from '../utils/experienceRange';
import { fadeInUp, stagger } from '../utils/motion';

/**
 * Public company profile page for non-bot users.
 * Bots get the Flask SSR version with Organization JSON-LD and full job listing.
 * Slug-mismatch auto-redirect mirrors the JobDetail pattern.
 */

const CompanyProfile: React.FC = () => {
  const { slug } = useParams<{ slug?: string }>();
  const navigate = useNavigate();
  const id = parseJobIdFromSlug(slug);

  const [company, setCompany] = React.useState<Company | null>(null);
  const [jobs, setJobs] = React.useState<Job[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!id) {
      setError('Invalid company URL');
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const [companyResp, jobsResp] = await Promise.all([
          companiesAPI.getCompany(id),
          companiesAPI.getCompanyJobs(id, { per_page: 30 }),
        ]);
        if (cancelled) return;
        setCompany(companyResp.company);
        setJobs(jobsResp.jobs || []);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load company');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Canonical-URL redirect: if user lands on /companies/12 or wrong slug, swap to canonical
  React.useEffect(() => {
    if (!company || !slug) return;
    const canonical = companyUrl(company).replace(/^\/companies\//, '');
    if (slug !== canonical) {
      navigate(`/companies/${canonical}`, { replace: true });
    }
  }, [company, slug, navigate]);

  const baseUrl = process.env.REACT_APP_PUBLIC_BASE_URL || 'https://talixjobs.com';

  if (loading) {
    return (
      <div className="container portal-v3-page">
        <Card padded>
          <div style={{ textAlign: 'center', padding: 'var(--s-6)', color: '#64748b' }}>
            Loading company profile…
          </div>
        </Card>
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="container portal-v3-page">
        <SEO title="Company not found" noIndex />
        <Card padded>
          <SectionHeader title="Company not found" description={error || 'This company profile is no longer available.'} />
          <Link to="/jobs" className="ui-btn ui-btn--primary ui-btn--sm">
            <span className="ui-btn__label">Browse all jobs</span>
          </Link>
        </Card>
      </div>
    );
  }

  const canonicalUrl = `${baseUrl}${companyUrl(company)}`;
  const activeJobs = jobs.filter((j) => j.is_active);

  const orgJsonLd = {
    '@context': 'https://schema.org/',
    '@type': 'Organization',
    name: company.name,
    url: canonicalUrl,
    ...(company.website ? { sameAs: company.website } : {}),
    ...(company.logo_url ? { logo: company.logo_url } : {}),
    ...(company.description ? { description: company.description.slice(0, 500) } : {}),
  };

  return (
    <div className="container portal-v3-page">
      <SEO
        title={`${company.name} — Open Jobs`}
        description={
          company.description
            ? company.description.slice(0, 160)
            : `Open positions at ${company.name}. ${activeJobs.length} active role${activeJobs.length === 1 ? '' : 's'} on talixjobs.`
        }
        canonicalUrl={canonicalUrl}
        ogImage={company.logo_url || undefined}
        jsonLd={orgJsonLd}
      />

      <SectionHeader
        eyebrow="Company"
        title={company.name}
        description={[
          company.location || null,
          company.industry || null,
          company.size ? `${company.size} employees` : null,
        ].filter(Boolean).join(' · ') || undefined}
      />

      {company.description && (
        <Card padded>
          <SectionHeader eyebrow="About" title={`About ${company.name}`} />
          <p style={{ color: '#334155', lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>{company.description}</p>
          {company.website && (
            <p style={{ marginTop: 'var(--s-3)' }}>
              <a
                href={company.website}
                target="_blank"
                rel="noopener noreferrer nofollow"
                style={{ color: '#1d4ed8' }}
              >
                {company.website}
              </a>
            </p>
          )}
        </Card>
      )}

      <Card padded>
        <SectionHeader
          eyebrow="Open positions"
          title={activeJobs.length === 0 ? 'No active roles right now' : `${activeJobs.length} active role${activeJobs.length === 1 ? '' : 's'}`}
        />
        {activeJobs.length === 0 ? (
          <EmptyState
            title="Nothing open at this company"
            description="Check back later or browse other companies on talixjobs."
            icon={<Icon name="briefcase" size={28} />}
            action={
              <Link to="/jobs" className="ui-btn ui-btn--primary ui-btn--sm">
                <span className="ui-btn__label">Browse all jobs</span>
              </Link>
            }
          />
        ) : (
          <motion.div variants={stagger(0.04)} initial="hidden" animate="visible" className="location-landing__list">
            {activeJobs.map((job) => (
              <motion.div key={job.id} variants={fadeInUp}>
                <Card padded className="location-landing__row">
                  <div className="location-landing__row-main">
                    <Link to={jobUrl(job)} className="location-landing__title">{job.title}</Link>
                    <div className="location-landing__sub">
                      {job.location && <span>{job.location}</span>}
                      {job.job_type && (<><span>•</span><span>{job.job_type}</span></>)}
                      {hasExperienceRange(job) && (<><span>•</span><span>{formatJobExperience(job)}</span></>)}
                      {(job.salary_min || job.salary_max) && (
                        <>
                          <span>•</span>
                          <span>
                            {job.salary_currency || 'INR'}{' '}
                            {job.salary_min ?? ''}
                            {job.salary_min && job.salary_max ? '–' : ''}
                            {job.salary_max ?? ''}
                          </span>
                        </>
                      )}
                    </div>
                    <div className="location-landing__skills">
                      {(job.skills || []).slice(0, 5).map((s) => (
                        <Pill key={s.id} tone="neutral">{s.skill?.name}</Pill>
                      ))}
                      {job.remote_allowed && <Pill tone="success">Remote OK</Pill>}
                    </div>
                  </div>
                  <div className="location-landing__row-side">
                    <Button
                      variant="primary"
                      size="sm"
                      iconRight={<Icon name="chevron-right" size={14} />}
                      onClick={() => (window.location.href = jobUrl(job))}
                    >
                      View
                    </Button>
                  </div>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        )}
      </Card>

      <Card padded>
        <SectionHeader eyebrow="More to explore" title="Related searches" />
        <div className="location-landing__related">
          <Link to="/jobs" className="ui-btn ui-btn--secondary ui-btn--sm">
            <span className="ui-btn__label">All jobs across India</span>
          </Link>
          <Link to="/" className="ui-btn ui-btn--ghost ui-btn--sm">
            <span className="ui-btn__label">talixjobs home</span>
          </Link>
        </div>
      </Card>
    </div>
  );
};

export default CompanyProfile;
